/**
 * useFocusTrap — keep Tab and Shift+Tab inside a modal panel.
 *
 * Extracted from `WorkshopDayDrawer`, which owned the only copy. Without a
 * trap, Tab walks straight out of an open dialog into the page behind it, so
 * a keyboard user can operate controls the modal is supposed to be blocking —
 * which matters most on the dialogs that do something irreversible.
 *
 * Initial focus is opt-in: some dialogs want the first focusable element,
 * others (a form with one obvious field) focus that field themselves and would
 * be fighting this hook for it.
 */
import { useEffect } from 'react';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function useFocusTrap(
  panelRef: React.RefObject<HTMLElement>,
  options: { autoFocusFirst?: boolean } = {},
): void {
  const { autoFocusFirst = false } = options;

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = () =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled'),
      );
    if (autoFocusFirst) focusables()[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      // Re-query on every Tab rather than caching: the panel's contents change
      // (the form is replaced by a receipt, buttons enable and disable), and a
      // stale list would trap focus on an element that is no longer there.
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    panel.addEventListener('keydown', onKeyDown);
    return () => panel.removeEventListener('keydown', onKeyDown);
  }, [panelRef, autoFocusFirst]);
}
